import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    Box,
    Button,
    Flex,
    Icon,
    IconButton,
    Spinner,
    Text,
    Textarea,
    useToast,
    VStack,
} from '@chakra-ui/react'
import { useLocation } from '@reach/router'
import { FaArrowUp } from 'react-icons/fa6'

import { useUserContext } from '~config/user-context'
import { useClinic } from '~contexts/clinic-context'
import AssistantMessageContent from '~components/admin/tools/support-bot/assistant-message-content'
import {
    SupportBotConversationFieldsFragment,
    SupportBotMessageRole,
    SupportBotRunStatus,
    useClinicAgentConversationQuery,
    useClinicAgentRunStatusQuery,
    useSendClinicAgentMessageMutation,
} from '~graphql/generated/graphql'
import { AskChckvetBetaNotice } from './ask-chckvet-beta'
import { useAskChckvetDock } from './ask-chckvet-context'
import AskChckvetThinking from './ask-chckvet-thinking'
import { buildClinicAgentUiContext } from './clinic-agent-ui-context'
import { getAskChckvetStarters } from './ask-chckvet-starters'
import {
    askChckvetUsageBlockMessage,
    getAskChckvetUsage,
    recordAskChckvetMessage,
    type AskChckvetUsage,
} from './ask-chckvet-usage'

type PendingRun = {
    runId: string
    conversationId: string
}

const COMPOSER_MIN_HEIGHT_PX = 38
const COMPOSER_MAX_HEIGHT_PX = 320

/**
 * Message list + composer for the Ask Chckvet dock.
 *
 * Owns one thread at a time, selected by `activeConversationId` on the dock
 * context. Diagnosis is read-only via a delegated JWT; config fixes arrive as
 * Apply cards rendered by `AssistantMessageContent`.
 */
export default function AskChckvetThread() {
    const { clinic } = useClinic()
    const { userId } = useUserContext()
    const clinicId = clinic?.id
    const location = useLocation()
    const toast = useToast()

    const {
        activeConversationId,
        setActiveConversationId,
        seedPrompt,
        autoSendSeed,
        consumeSeedPrompt,
    } = useAskChckvetDock()

    const [draft, setDraft] = useState('')
    const [conversation, setConversation] =
        useState<SupportBotConversationFieldsFragment | null>(null)
    const [pending, setPending] = useState<PendingRun | null>(null)
    const [usage, setUsage] = useState<AskChckvetUsage | null>(null)

    const bottomRef = useRef<HTMLDivElement>(null)
    const composerRef = useRef<HTMLTextAreaElement>(null)
    // Which thread id local state already reflects, so a background refetch
    // never rewinds the view to an older copy mid-run.
    const hydratedIdRef = useRef<string | null>(null)

    /**
     * Auto-grow the composer with its content (capped), while still letting
     * staff manually drag it taller via the native vertical resize handle.
     * Manual height persists until the next keystroke, which is the standard
     * chat-composer behaviour.
     */
    const resizeComposer = useCallback((): void => {
        const el = composerRef.current
        if (!el) return
        el.style.height = 'auto'
        const nextHeight = Math.min(
            Math.max(el.scrollHeight, COMPOSER_MIN_HEIGHT_PX),
            COMPOSER_MAX_HEIGHT_PX,
        )
        el.style.height = `${nextHeight}px`
    }, [])

    useEffect(() => {
        resizeComposer()
    }, [draft, resizeComposer])

    const [sendMessage, { loading: sending }] =
        useSendClinicAgentMessageMutation({
            refetchQueries: ['myClinicAgentConversations'],
        })

    const { data: conversationData, loading: loadingConversation } =
        useClinicAgentConversationQuery({
            variables: {
                clinicId: clinicId ?? '',
                id: activeConversationId ?? '',
            },
            skip: !clinicId || !activeConversationId,
            fetchPolicy: 'cache-and-network',
        })

    const { data: runData } = useClinicAgentRunStatusQuery({
        variables: {
            clinicId: clinicId ?? '',
            runId: pending?.runId ?? '',
        },
        skip: !clinicId || !pending?.runId,
        pollInterval: pending ? 1500 : 0,
        fetchPolicy: 'network-only',
    })

    // Local state can lag the selection by a render; never render or reply
    // into a thread the user has already navigated away from.
    const activeConversation =
        conversation && conversation.id === activeConversationId
            ? conversation
            : null

    // Selection changed: drop anything that belonged to the previous thread so
    // a poll that lands late can't write into the thread now on screen.
    useEffect(() => {
        if (!activeConversationId) {
            hydratedIdRef.current = null
        }
        setPending(previous =>
            previous && previous.conversationId === activeConversationId
                ? previous
                : null,
        )
    }, [activeConversationId])

    // Hydrate a newly selected thread, and resume polling if a run is still
    // in flight (started in another tab, or before a reload).
    useEffect(() => {
        const loaded = conversationData?.clinicAgentConversation
        if (!loaded || loaded.id !== activeConversationId) return
        if (hydratedIdRef.current === loaded.id) return

        hydratedIdRef.current = loaded.id
        setConversation(loaded)
        setPending(
            loaded.pendingRunId
                ? { runId: loaded.pendingRunId, conversationId: loaded.id }
                : null,
        )
    }, [conversationData, activeConversationId])

    useEffect(() => {
        if (!clinicId || !userId) {
            setUsage(null)
            return
        }
        setUsage(getAskChckvetUsage(userId, clinicId))
    }, [clinicId, userId])

    useEffect(() => {
        const snapshot = runData?.clinicAgentRunStatus
        if (!snapshot || !pending) return
        if (snapshot.status === SupportBotRunStatus.Running) return

        if (snapshot.conversation) {
            setConversation(snapshot.conversation)
            hydratedIdRef.current = snapshot.conversation.id
        }
        setPending(null)

        if (
            snapshot.status === SupportBotRunStatus.RunError ||
            snapshot.status === SupportBotRunStatus.Timeout ||
            snapshot.status === SupportBotRunStatus.NotFound
        ) {
            toast({
                title: 'Ask Chckvet hit an error',
                description: snapshot.errorMessage ?? snapshot.status,
                status: 'error',
                duration: 6000,
                isClosable: true,
            })
        }
    }, [runData, pending, toast])

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [
        activeConversation?.messages.length,
        pending,
        sending,
        runData?.clinicAgentRunStatus.assistantTextSoFar,
    ])

    // A prompt handed over from universal search / a starter chip / New audience.
    useEffect(() => {
        if (!seedPrompt) return
        const prompt = seedPrompt
        const shouldAutoSend = autoSendSeed
        consumeSeedPrompt()
        if (shouldAutoSend) {
            void sendPrompt(prompt)
            return
        }
        setDraft(prompt)
        composerRef.current?.focus()
        // sendPrompt is recreated often; this effect should only run when a
        // new seed arrives.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seedPrompt, autoSendSeed, consumeSeedPrompt])

    const starters = useMemo(
        () => getAskChckvetStarters(location.pathname),
        [location.pathname],
    )

    const usageBlockMessage = usage ? askChckvetUsageBlockMessage(usage) : null
    const atUsageCap = Boolean(usageBlockMessage)
    const canSend = Boolean(
        clinicId && userId && draft.trim() && !pending && !atUsageCap,
    )

    async function sendPrompt(rawMessage: string) {
        if (!clinicId || !userId) return
        const message = rawMessage.trim()
        if (!message || sending || pending) return

        const currentUsage = getAskChckvetUsage(userId, clinicId)
        setUsage(currentUsage)
        const blockMessage = askChckvetUsageBlockMessage(currentUsage)
        if (blockMessage) {
            toast({
                title: 'Monthly limit reached',
                description: blockMessage,
                status: 'warning',
                duration: 7000,
                isClosable: true,
            })
            return
        }

        setDraft('')
        try {
            const result = await sendMessage({
                variables: {
                    input: {
                        clinicId,
                        message,
                        conversationId: activeConversation?.id,
                        uiContext: buildClinicAgentUiContext(location),
                    },
                },
            })
            const started = result.data?.sendClinicAgentMessage
            if (!started) {
                throw new Error('No response from Ask Chckvet')
            }
            setUsage(recordAskChckvetMessage(userId, clinicId))
            hydratedIdRef.current = started.conversation.id
            setConversation(started.conversation)
            setActiveConversationId(started.conversationId)
            setPending({
                runId: started.runId,
                conversationId: started.conversationId,
            })
        } catch (err) {
            setDraft(message)
            toast({
                title: 'Could not send message',
                description:
                    err instanceof Error ? err.message : 'Unknown error',
                status: 'error',
                duration: 6000,
                isClosable: true,
            })
        }
    }

    async function handleSend() {
        await sendPrompt(draft)
    }

    const liveAssistantText =
        pending &&
        runData?.clinicAgentRunStatus.status === SupportBotRunStatus.Running
            ? runData.clinicAgentRunStatus.assistantTextSoFar
            : null

    const messages = activeConversation?.messages ?? []
    const showEmptyState =
        !activeConversationId && !activeConversation && !pending

    return (
        <Flex flexDir="column" flex={1} minH={0}>
            <VStack
                flex={1}
                minH={0}
                align="stretch"
                spacing={3}
                overflowY="auto"
                px={4}
                py={4}
            >
                {showEmptyState && (
                    <AskChckvetEmptyState
                        starters={starters}
                        onPick={prompt => {
                            setDraft(prompt)
                            composerRef.current?.focus()
                        }}
                    />
                )}
                {loadingConversation && !activeConversation && (
                    <Flex align="center" gap={2} color="gray.500" py={4}>
                        <Spinner size="sm" />
                        <Text fontSize="sm">Loading chat…</Text>
                    </Flex>
                )}
                {messages.map((message, index) => (
                    <Box
                        key={`${message.timestamp}-${index}`}
                        alignSelf={
                            message.role === SupportBotMessageRole.User
                                ? 'flex-end'
                                : 'flex-start'
                        }
                        maxW={
                            message.role === SupportBotMessageRole.User
                                ? '90%'
                                : '100%'
                        }
                        bg={
                            message.role === SupportBotMessageRole.User
                                ? 'brand.50'
                                : 'transparent'
                        }
                        px={message.role === SupportBotMessageRole.User ? 3 : 0}
                        py={message.role === SupportBotMessageRole.User ? 2 : 0}
                        rounded="xl"
                    >
                        {message.role === SupportBotMessageRole.User ? (
                            <Text whiteSpace="pre-wrap" fontSize="sm">
                                {message.content}
                            </Text>
                        ) : (
                            <AssistantMessageContent
                                content={message.content}
                                conversationId={activeConversation?.id ?? null}
                            />
                        )}
                    </Box>
                ))}
                {(sending || pending) && (
                    <Box alignSelf="flex-start" maxW="100%" w="full">
                        {liveAssistantText && (
                            <AssistantMessageContent
                                content={liveAssistantText}
                                conversationId={activeConversation?.id ?? null}
                                isStreaming
                            />
                        )}
                        <AskChckvetThinking
                            startedAtMs={
                                runData?.clinicAgentRunStatus.startedAt
                            }
                            toolCallCount={
                                runData?.clinicAgentRunStatus.toolCallCount ??
                                0
                            }
                            isStreaming={Boolean(liveAssistantText)}
                        />
                    </Box>
                )}
                <div ref={bottomRef} />
            </VStack>

            <Box borderTopWidth={1} borderColor="gray.200" p={3}>
                <Flex
                    align="flex-end"
                    gap={2}
                    borderWidth={1}
                    borderColor="gray.200"
                    rounded="xl"
                    px={2}
                    py={2}
                    _focusWithin={{
                        borderColor: 'brand.300',
                        shadow: 'sm',
                    }}
                >
                    <Textarea
                        ref={composerRef}
                        value={draft}
                        onChange={event => setDraft(event.target.value)}
                        placeholder={
                            !clinicId
                                ? 'Select a clinic first'
                                : atUsageCap
                                  ? 'Monthly message limit reached'
                                  : 'Ask about this screen, a client, or a setting…'
                        }
                        rows={1}
                        resize="vertical"
                        border="none"
                        minH="38px"
                        maxH="320px"
                        overflowY="auto"
                        fontSize="sm"
                        p={1}
                        _focusVisible={{ boxShadow: 'none' }}
                        isDisabled={!clinicId || !!pending || atUsageCap}
                        onKeyDown={event => {
                            if (
                                event.key === 'Enter' &&
                                !event.shiftKey &&
                                !event.nativeEvent.isComposing
                            ) {
                                event.preventDefault()
                                void handleSend()
                            }
                        }}
                    />
                    <IconButton
                        aria-label="Send message"
                        icon={<Icon as={FaArrowUp} />}
                        size="sm"
                        rounded="full"
                        colorScheme="brand"
                        onClick={() => void handleSend()}
                        isLoading={sending}
                        isDisabled={!canSend}
                    />
                </Flex>
                {/* Remaining-message counts stay hidden while we're just
                    gauging usage; only surface the cap once it's actually
                    blocking a send. */}
                {usageBlockMessage && (
                    <Text fontSize="xs" color="orange.600" mt={2}>
                        {usageBlockMessage}
                    </Text>
                )}
            </Box>
        </Flex>
    )
}

function AskChckvetEmptyState({
    starters,
    onPick,
}: {
    starters: ReturnType<typeof getAskChckvetStarters>
    onPick: (prompt: string) => void
}) {
    return (
        <Box>
            <AskChckvetBetaNotice />
            <VStack align="stretch" spacing={2}>
                {starters.map(starter => (
                    <Button
                        key={starter.prompt}
                        size="sm"
                        variant="outline"
                        justifyContent="flex-start"
                        rounded="lg"
                        fontWeight="medium"
                        whiteSpace="normal"
                        textAlign="left"
                        h="auto"
                        py={2}
                        onClick={() => onPick(starter.prompt)}
                    >
                        {starter.prompt}
                    </Button>
                ))}
            </VStack>
        </Box>
    )
}
